import { Badge, Button, Icon } from '@/components/ui';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

import { REQUEST_STATUS_LABELS, REQUEST_STATUS_TONES, formatSlot } from './reschedule';

/**
 * A candidate's request for another time: the reason, the proposed times
 * (weekday and time, in the viewer's zone) and the note.
 *
 * For the company, an open request offers "Use this time" beside each slot
 * and the Decline / Accept pair underneath (`actions`). For the candidate it
 * reads as their own request, waiting or answered.
 */
export function RescheduleRequestPanel({
  request,
  audience = 'company',
  candidateName = 'The candidate',
  onUseSlot,
  actions,
  disabledReason = null,
  className,
}) {
  if (!request) return null;
  const open = request.status === 'OPEN';
  const forCompany = audience === 'company';
  const who = forCompany ? candidateName : 'You';

  return (
    <section
      aria-label="Request for another time"
      className={cn(
        'rounded-input mt-3 border p-3 text-sm',
        open ? 'border-warning-200 bg-warning-50/60' : 'border-line bg-surface-muted/50',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Icon name="repeat" size={14} className={open ? 'text-warning-700' : 'text-ink-muted'} />
        <span className="text-ink font-semibold">
          {open
            ? forCompany
              ? `${who} asked for another time`
              : 'Reschedule requested — waiting for the company'
            : forCompany
              ? 'Request for another time'
              : 'Your request for another time'}
        </span>
        {!open && (
          <Badge tone={REQUEST_STATUS_TONES[request.status] ?? 'neutral'}>
            {REQUEST_STATUS_LABELS[request.status]}
          </Badge>
        )}
        <span className="text-ink-muted text-xs">Asked {formatDate(request.created_at)}</span>
      </div>

      <blockquote className="text-ink border-warning-300 mt-2 border-l-2 pl-3 whitespace-pre-line">
        “{request.reason}”
      </blockquote>

      <div className="mt-3">
        <p className="text-ink-muted text-xs font-medium">
          {request.any_time
            ? 'Proposed times'
            : `Proposed ${request.proposed_slots.length === 1 ? 'time' : 'times'}`}
        </p>
        {request.any_time ? (
          <p className="text-ink mt-1">
            Any time works — {forCompany ? 'please suggest one.' : 'the company will suggest one.'}
          </p>
        ) : (
          <ul className="mt-1 space-y-1.5">
            {request.proposed_slots.map((slot) => (
              <li key={slot} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ink inline-flex items-center gap-1.5 font-medium tabular-nums">
                  <Icon name="calendarClock" size={14} className="text-ink-muted" />
                  {formatSlot(slot)}
                </span>
                {forCompany && open && onUseSlot && (
                  <Button
                    variant="tintBrand"
                    size="sm"
                    disabled={Boolean(disabledReason)}
                    title={disabledReason ?? `Move the interview to ${formatSlot(slot)}`}
                    onClick={() => onUseSlot(slot)}
                  >
                    Use this time
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {request.note && (
        <p className="text-ink-muted mt-3 text-xs whitespace-pre-line">
          <span className="text-ink font-medium">Note: </span>
          {request.note}
        </p>
      )}

      {request.company_reply && (
        <p className="text-ink-muted mt-3 text-xs whitespace-pre-line">
          <span className="text-ink font-medium">
            {forCompany ? 'Your reply' : 'The company replied'}:{' '}
          </span>
          {request.company_reply}
        </p>
      )}

      {open && actions && <div className="mt-3 flex flex-wrap justify-end gap-2">{actions}</div>}
    </section>
  );
}
